import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2, XCircle, Stethoscope, Loader2, RefreshCw, ClipboardCheck,
  AlertCircle, MapPin, Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateNL, formatHoursHM } from '@/lib/format';
import { REVIEW_REASON_LABELS } from './EditShiftDialog';

function LocationBlock({ loc, onApprove, onEditItem, onResolve, pendingKey }) {
  const allIds = useMemo(
    () => loc.employees.flatMap((e) => e.items.map((i) => i.assignment_id)),
    [loc],
  );
  const bulkBusy = pendingKey === `bulk-${loc.slug}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`approvals-location-${loc.slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <MapPin size={15} className="text-brand-navy" />
          <h3 className="text-sm font-semibold text-slate-900">{loc.name}</h3>
          {loc.approvable_count > 0 && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[11px]">
              {loc.approvable_count} goed te keuren
            </Badge>
          )}
          {loc.needs_review_count > 0 && (
            <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700 text-[11px]">
              {loc.needs_review_count} klok-review
            </Badge>
          )}
        </div>
        {allIds.length > 0 && (
          <Button
            size="sm"
            data-testid={`bulk-approve-${loc.slug}`}
            onClick={() => onApprove(allIds, 'approved', null, `bulk-${loc.slug}`)}
            disabled={!!pendingKey}
            className="h-8 bg-emerald-600 text-white hover:bg-emerald-700 text-[12px]"
          >
            {bulkBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />}
            Keur alles goed voor {loc.name}
          </Button>
        )}
      </div>

      <div className="divide-y divide-slate-100">
        {loc.employees.map((emp) => (
          <div key={emp.employee_id} className="px-4 py-3" data-testid={`approvals-emp-${emp.personeelsnummer}`}>
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
                {emp.name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
              </div>
              <div className="leading-tight">
                <p className="text-sm font-medium text-slate-900">{emp.name}</p>
                <p className="text-[11px] text-slate-500">#{emp.personeelsnummer} · {emp.role || 'medewerker'}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {emp.items.map((it) => {
                const busy = pendingKey === `item-${it.assignment_id}`;
                return (
                  <div
                    key={it.assignment_id}
                    data-testid={`approvals-item-${it.assignment_id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-1.5"
                  >
                    <div className="flex items-center gap-3 text-sm text-slate-700">
                      <span className="font-medium tabular-nums">{formatDateNL(it.date)}</span>
                      <span className="tabular-nums text-slate-500">{it.start_time}–{it.end_time}</span>
                      <span className="tabular-nums text-slate-500">{formatHoursHM(it.duration_net)}</span>
                      {it.is_hulpvraag && (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100 text-[10px]">Hulpvraag</Badge>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        data-testid={`review-edit-${it.assignment_id}`}
                        onClick={() => onEditItem({ ...it, name: emp.name, personeelsnummer: emp.personeelsnummer })}
                        disabled={!!pendingKey}
                        className="h-7 px-2 text-[11px]"
                      >
                        <Pencil size={12} className="mr-1" /> Bewerk
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`approve-worked-${it.assignment_id}`}
                        onClick={() => onApprove([it.assignment_id], 'approved', null, `item-${it.assignment_id}`)}
                        disabled={!!pendingKey}
                        className="h-7 px-2 text-[11px]"
                      >
                        {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />}
                        Gewerkt
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`approve-sick-${it.assignment_id}`}
                        onClick={() => onApprove([it.assignment_id], 'approved', 'ziek', `item-${it.assignment_id}`)}
                        disabled={!!pendingKey}
                        className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <Stethoscope size={12} className="mr-1" /> Ziek
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`approve-rejected-${it.assignment_id}`}
                        onClick={() => onApprove([it.assignment_id], 'rejected', 'niet verschenen', `item-${it.assignment_id}`)}
                        disabled={!!pendingKey}
                        className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                      >
                        <XCircle size={12} className="mr-1" /> Niet versch.
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {loc.needs_review.length > 0 && (
          <div className="bg-rose-50/40 px-4 py-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-rose-700">
              <AlertCircle size={13} /> Klok-registratie te reviewen ({loc.needs_review.length})
            </p>
            <div className="space-y-1">
              {loc.needs_review.map((r) => {
                const resolveBusy = pendingKey === `resolve-${r.time_record_id}`;
                return (
                  <div key={r.time_record_id} className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-slate-600" data-testid={`approvals-review-${r.time_record_id}`}>
                    <span>
                      {r.employee_name} <span className="text-slate-400">#{r.personeelsnummer}</span>
                      <span className="ml-2 text-[11px] text-rose-600">{REVIEW_REASON_LABELS[r.review_reason] || 'Klok-review'}</span>
                    </span>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        data-testid={`review-correct-${r.time_record_id}`}
                        onClick={() => onEditItem({ ...r, name: r.employee_name })}
                        disabled={!!pendingKey}
                        className="h-6 px-1.5 text-[10px]"
                      >
                        <Pencil size={11} className="mr-0.5" /> Bewerk
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`review-resolve-${r.time_record_id}`}
                        onClick={() => onResolve(r.time_record_id)}
                        disabled={!!pendingKey}
                        className="h-6 px-1.5 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      >
                        {resolveBusy ? <Loader2 size={11} className="mr-0.5 animate-spin" /> : <CheckCircle2 size={11} className="mr-0.5" />}
                        Afgehandeld
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Cross-location review panel — the consolidated "Uren goedkeuren" flow.
 * Groups pending approvals per location with per-location bulk approve.
 * Edit reuses the parent's shift-edit dialog via onEditItem.
 */
export function UrenReviewPanel({ globalLocation, onEditItem, onChanged, reloadKey }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/uren/pending-approvals');
      setData(data);
    } catch {
      toast({ title: 'Kon goedkeuringen niet laden', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const onApprove = async (assignmentIds, newStatus, reason, key) => {
    if (!assignmentIds || assignmentIds.length === 0) return;
    setPendingKey(key);
    try {
      const { data: res } = await api.post('/uren/approve', {
        assignment_ids: assignmentIds,
        new_status: newStatus,
        reason: reason || null,
      });
      if (res.blocked_locked_or_403 > 0) {
        toast({
          title: 'Deels geblokkeerd',
          description: `${res.updated} bijgewerkt, ${res.blocked_locked_or_403} geblokkeerd (afgesloten periode of geen toegang).`,
        });
      } else {
        toast({ title: `${res.updated} bijgewerkt` });
      }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Bijwerken mislukt';
      toast({ title: typeof msg === 'string' ? msg : 'Bijwerken mislukt', variant: 'destructive' });
    } finally {
      setPendingKey(null);
    }
  };

  const onResolve = async (timeRecordId) => {
    setPendingKey(`resolve-${timeRecordId}`);
    try {
      await api.post(`/time-records/${timeRecordId}/resolve-review`, {});
      toast({ title: 'Afgehandeld' });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Afhandelen mislukt';
      toast({ title: typeof msg === 'string' ? msg : 'Afhandelen mislukt', variant: 'destructive' });
    } finally {
      setPendingKey(null);
    }
  };

  const visibleLocations = useMemo(() => {
    if (!data?.locations) return [];
    if (globalLocation && globalLocation !== 'all') {
      return data.locations.filter((l) => l.slug === globalLocation);
    }
    return data.locations;
  }, [data, globalLocation]);

  const visibleTotal = useMemo(
    () => visibleLocations.reduce((s, l) => s + l.approvable_count + l.needs_review_count, 0),
    [visibleLocations],
  );

  return (
    <div className="space-y-4" data-testid="uren-review-panel">
      <div className="flex items-center justify-between gap-2">
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" data-testid="approvals-total-count">
          <ClipboardCheck size={13} className="mr-1.5" /> {visibleTotal} openstaand
        </Badge>
        <Button type="button" variant="outline" size="sm" data-testid="approvals-refresh" onClick={load} disabled={loading} className="h-9">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span className="ml-1.5">Verversen</span>
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-slate-500" data-testid="approvals-loading">
          <Loader2 size={18} className="mr-2 animate-spin" /> Laden…
        </div>
      ) : visibleLocations.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center" data-testid="approvals-empty">
          <CheckCircle2 size={36} className="mx-auto mb-3 text-emerald-400" />
          <p className="text-sm font-medium text-slate-700">Alles goedgekeurd</p>
          <p className="mt-1 text-xs text-slate-500">Geen openstaande uur-goedkeuringen.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleLocations.map((loc) => (
            <LocationBlock key={loc.slug} loc={loc} onApprove={onApprove} onEditItem={onEditItem} onResolve={onResolve} pendingKey={pendingKey} />
          ))}
        </div>
      )}
    </div>
  );
}
